<template>
  <div
    class="cart-drawer"
    :class="{ 'has-products': cart.item_count > 0 }"
  >
    <h2
      class="cart-drawer__title"
      :class="{'no-free-shipping': !freeShippingEnabled}"
      v-text="$string('cart.title')"
    />

    <free-shipping-notification
      v-if="freeShippingEnabled && cart.item_count > 0"
      :free-shipping-threshold="freeShippingThreshold"
    />

    <div class="cart-drawer__inner">
      <form
        v-if="cart.item_count > 0"
        class="cart-drawer__body"
        :action="$variable('routes.cart.url')"
        method="post"
      >
        <div class="cart-drawer__products">
          <line-item
            v-for="item, index of papItems"
            :key="getCartItemKey(item)"
            block-class="cart-drawer"
            :index="index"
            :item="item"
            :threshold="papThreshold"
            :discount="papDiscount"
            :collection="papCollection"
            :qualifies="qualifies"
          />

          <pickapack-message
            v-if="papItems.length"
            :items="papItems"
            :threshold="papThreshold"
            :discount="papDiscount"
            :collection="papCollection"
          />

          <div
            v-if="papItems.length && cartItems.length"
            class="line-item__spacer"
          />

          <line-item
            v-for="item, index of cartItems"
            :key="getCartItemKey(item)"
            block-class="cart-drawer"
            :index="index"
            :item="item"
            :show-final-sale-message="false"
          />
        </div>

        <div class="cart-drawer__footer">
          <p
            v-if="qualifies"
            class="cart-drawer__subtitle"
            :class="{ [$class('loading')]: cart.item_count === 0 }"
          >
            <span
              class="cart-drawer__items body-2"
              v-text="$string(
                'product.extra.pick_a_pack_promotion',
                {
                  replace: {
                    dis: papDiscount,
                  }
                }
              )"
            />

            <span
              class="cart-drawer__price body-2"
              v-text="papDiscountedAmount"
            />
          </p>

          <p
            class="cart-drawer__subtitle"
            :class="{ [$class('loading')]: cart.item_count === 0 }"
          >
            <span
              class="cart-drawer__items body-2"
              v-text="$string('customers.order.lines.subtotal')"
            />

            <span
              class="cart-drawer__price body-2"
              v-text="cartTotalPrice"
            />
          </p>

          <div class="cart-drawer__button-wrap">
            <btn
              class="cart-drawer__view-cart"
              modifiers="solid block center"
              :show-icon="false"
              :label="$string('cart.view_cart')"
              :url="$variable('routes.cart.url')"
            />

            <btn
              class="cart-drawer__continue"
              modifiers="outlined block center"
              :show-icon="false"
              :label="$string('cart.continue_shopping')"
              @click.prevent="closeOverlay('cartDrawer')"
            />
          </div>

          <section v-if="cart.item_count > 0 && isCrossSellEnabled && crossSellCollectionHandle">
            <cart-cross-sell
              :source="crossSellSource"
              :collection-handle="crossSellCollectionHandle"
              :product-limit="crossSellProductLimit"
              :title="crossSellTitle"
              :nosto-placement-id="crossSellNostoPlacementId"
            />
          </section>
        </div>
      </form>

      <div
        v-else
        class="cart-drawer__empty"
      >
        <p class="cart-drawer__subtitle">
          <span
            class="cart-drawer__items body-2"
            v-text="$string('customers.order.lines.subtotal')"
          />

          <span
            class="cart-drawer__price body-2"
            v-text="cartTotalPrice"
          />
        </p>

        <p
          class="cart-drawer__empty-text body-1"
          v-text="$string('cart.empty.text')"
        />

        <btn
          class="cart-drawer__continue"
          type="button"
          modifiers="outlined block center"
          :show-icon="false"
          :label="$string('cart.continue_shopping')"
          @click="closeOverlay('cartDrawer')"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart drawer.
 *
 * @param {Number} papThreshold - Number of items to qualify for discount.
 * @param {Number} papDiscount - Percentage of discount.
 * @param {String} papCollection - Pick-a-Pack collection.
 * @param {Boolean} freeShippingEnabled - If free shipping is enabled.
 * @param {Number} freeShippingThreshold - Dollar amount to qualify.
 *
 */

import { nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'
import { sortCartItems } from '~/helpers/general'

import Btn from '~global/btn/btn'
import LineItem from '~async/line-item/line-item'
import PickapackMessage from '~async/pickapack-message/pickapack-message'
import CartCrossSell from '~async/cart-cross-sell/cart-cross-sell'
import FreeShippingNotification from '~async/free-shipping-notification/free-shipping-notification'

export default {
  name: 'CartDrawer',

  components: {
    Btn,
    CartCrossSell,
    LineItem,
    PickapackMessage,
    FreeShippingNotification,
  },

  props: {
    papThreshold: {
      type: Number,
      default: 3,
    },
    papDiscount: {
      type: Number,
      default: 15,
    },
    papCollection: {
      type: String,
      default: '',
    },
    freeShippingEnabled: {
      type: Boolean,
      default: true,
    },
    freeShippingThreshold: {
      type: Number,
      default: 50,
    },
    isCrossSellEnabled: {
      type: Boolean,
      default: false,
    },
    crossSellSource: {
      type: String,
      default: 'collection',
    },
    crossSellCollectionHandle: {
      type: String,
      default: '',
    },
    crossSellProductLimit: {
      type: Number,
      default: 12,
    },
    crossSellTitle: {
      type: String,
      default: '',
    },
    crossSellNostoPlacementId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      qualifies: false,
      papDiscountedAmount: '',
      cartTotalPrice: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
    }),

    /**
     * Compute cart items.
     * @returns {Array}
     */
    cartItems() {
      return sortCartItems(this.cart.items).nonPapItems
    },

    /**
     * Compute Pick-a-pack items.
     * @returns {Array}
     */
    papItems() {
      return sortCartItems(this.cart.items).papItems
    },

    /**
     * Computes header items string.
     * @returns {String}
     */
    itemsString() {
      const string = this.$string(
        'cart.items',
        { pluralise: this.cart.item_count },
      )

      return `${this.cart.item_count} ${string}`
    },
  },

  watch: {

    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      this.updateFocus()
      this.doesQualify()
    },
  },

  mounted() {
    this.updateFocus()
    this.doesQualify()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @returns {String}
     */
    getCartItemKey(item) {
      return `cart-line-item-${item.key}`
    },

    /**
     * Update focus trap and scroll lock.
     */
    updateFocus() {
      nextTick().then(() => {
        cnvs.EventBus.emit('overlay:update', 'cartDrawer')
      })
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.openOverlay({ namespace: 'cartDrawer' })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('cartDrawer')
    },

    /**
     * Calculate pick-a-pack savings in footer if cart qualifies.
     */
    calculateSavings() {
      let savings = 0

      this.papItems.forEach((item) => {
        const { price, quantity } = item
        const total = (price * quantity) / 100
        const discount = this.papDiscount / 100
        const discountedAmount = total * discount

        savings += discountedAmount
      })

      this.papDiscountedAmount = `-$${savings.toFixed(2)}`
      this.cartTotalPrice = `$${((this.cart.total_price / 100) - savings.toFixed(2)).toFixed(2)}`
    },

    /**
     * Determine if PAP promotion applies to cart.
     */
    doesQualify() {
      let count = 0

      this.papItems.forEach((item) => {
        count += item.quantity
      })

      this.qualifies = count >= this.papThreshold

      if (!this.qualifies) {
        this.cartTotalPrice = this.$formatMoney(this.cart.total_price)

        return
      }

      this.calculateSavings()
    },
  },
}
</script>
